import type { ModuleMount, ModuleRoute, NavigationEntry } from '../module';

export type RouteParams = Record<string, string>;

export interface RegisteredRoute {
  route: ModuleRoute;
  mount: ModuleMount;
  /** Absolute path within the shell, starting with `/`, with `:param` placeholders. */
  pattern: string;
}

/**
 * The navigation registry and route table for one shell. Built once from the shell's mounts. Modules never see
 * each other's paths: they ask for a route id and get back wherever this shell mounted it, or null if this shell
 * does not compose that module.
 */
export interface RouteRegistry {
  routes: readonly RegisteredRoute[];
  navigation: readonly NavigationEntry[];
  /** Path for a route id, with params filled in, or null when no mounted module owns it. */
  href: (routeId: string, params?: RouteParams) => string | null;
}

function join(...segments: string[]): string {
  const path = segments
    .flatMap((segment) => segment.split('/'))
    .filter(Boolean)
    .join('/');
  return `/${path}`;
}

export function createRouteRegistry(mounts: readonly ModuleMount[]): RouteRegistry {
  const routes: RegisteredRoute[] = [];
  const byId = new Map<string, RegisteredRoute>();
  const basePaths = new Set<string>();

  for (const mount of mounts) {
    if (basePaths.has(mount.basePath)) throw new Error(`Two modules are mounted at "${mount.basePath}".`);
    basePaths.add(mount.basePath);
    for (const route of mount.module.routes) {
      if (byId.has(route.id)) throw new Error(`Route id "${route.id}" is declared twice.`);
      const registered = { route, mount, pattern: join(mount.basePath, route.path) };
      routes.push(registered);
      byId.set(route.id, registered);
    }
  }

  const navigation = mounts
    .flatMap((mount) => mount.module.navigation)
    .filter((entry) => byId.has(entry.routeId))
    .sort((a, b) => a.order - b.order);

  const href = (routeId: string, params: RouteParams = {}): string | null => {
    const registered = byId.get(routeId);
    if (!registered) return null;
    return registered.pattern.replace(/:([A-Za-z0-9_]+)/g, (_match, name: string) => {
      const value = params[name];
      if (value === undefined) throw new Error(`Route "${routeId}" needs the "${name}" parameter.`);
      return encodeURIComponent(value);
    });
  };

  return { routes, navigation, href };
}
